/**
 * URI resolver — parses and resolves $ref URIs in JSON definitions.
 *
 * Supported URI schemes:
 *   module://participant#properties/firstName  → modules/participant/participant.domain.json
 *   shared://model/address#fields/city         → shared/address/address.model.json
 *   shared://orch/address#addressSection       → shared/address/address.orch.json (when present)
 *   shared://rules/address#templates/zipLookup → shared/address/address.rules.json
 *   #/definitions/address                      → local document → definitions.address
 *   #/properties/firstName                     → local document → properties.firstName
 */

import type { ParsedRef, FormLoaderContext } from './types';

export function parseRef(uri: string): ParsedRef {
  if (uri.startsWith('module://')) {
    const rest = uri.slice('module://'.length);
    const hashIdx = rest.indexOf('#');
    if (hashIdx === -1) {
      return { scheme: 'module', path: rest, fragment: '' };
    }
    return { scheme: 'module', path: rest.slice(0, hashIdx), fragment: rest.slice(hashIdx + 1) };
  }

  if (uri.startsWith('shared://')) {
    const rest = uri.slice('shared://'.length);
    const hashIdx = rest.indexOf('#');
    if (hashIdx === -1) {
      return { scheme: 'shared', path: rest, fragment: '' };
    }
    return { scheme: 'shared', path: rest.slice(0, hashIdx), fragment: rest.slice(hashIdx + 1) };
  }

  if (uri === '#') {
    return { scheme: 'local', path: '', fragment: '' };
  }

  if (uri.startsWith('#/')) {
    return { scheme: 'local', path: '', fragment: uri.slice(2) };
  }

  throw new Error(
    `[uriResolver] Unknown $ref scheme: "${uri}". Expected module://, shared://, or #/`,
  );
}

export function resolveRef(uri: string, ctx: FormLoaderContext, localDoc?: Record<string, unknown>): unknown {
  const parsed = parseRef(uri);

  switch (parsed.scheme) {
    case 'module':
      return resolveModuleRef(parsed, ctx);
    case 'shared':
      return resolveSharedRef(parsed, ctx);
    case 'local':
      if (!localDoc) {
        throw new Error(`[uriResolver] Local $ref "${uri}" requires a document context`);
      }
      return navigatePath(localDoc, parsed.fragment);
    default: {
      const _exhaustive: never = parsed.scheme;
      throw new Error(`[uriResolver] Unknown scheme: ${_exhaustive}`);
    }
  }
}

function resolveModuleRef(parsed: ParsedRef, ctx: FormLoaderContext): unknown {
  const file = ctx.moduleDomain.get(parsed.path);
  if (!file) {
    throw new Error(
      `[uriResolver] Module domain not found: "${parsed.path}". ` +
        `Available: [${Array.from(ctx.moduleDomain.keys()).join(', ')}]`,
    );
  }
  if (!parsed.fragment) return file;
  return navigatePath(file as unknown as Record<string, unknown>, parsed.fragment);
}

function resolveSharedRef(parsed: ParsedRef, ctx: FormLoaderContext): unknown {
  const parts = parsed.path.split('/');
  const tier = parts[0];
  const name = parts.slice(1).join('/');

  switch (tier) {
    case 'model': {
      const file = ctx.sharedModel.get(name);
      if (!file) {
        throw new Error(`[uriResolver] Shared model file not found: "${name}"`);
      }
      if (!parsed.fragment) return file;
      return navigatePath(file as unknown as Record<string, unknown>, parsed.fragment);
    }
    case 'orch': {
      const file = ctx.sharedOrch.get(name);
      if (!file) {
        throw new Error(`[uriResolver] Shared orch file not found: "${name}"`);
      }
      if (!parsed.fragment) return file;
      return navigatePath(file as unknown as Record<string, unknown>, parsed.fragment);
    }
    case 'rules': {
      const file = ctx.sharedRules.get(name);
      if (!file) {
        throw new Error(`[uriResolver] Shared rules file not found: "${name}"`);
      }
      if (!parsed.fragment) return file;
      return navigatePath(file as unknown as Record<string, unknown>, parsed.fragment);
    }
    default:
      throw new Error(`[uriResolver] Unknown shared tier: "${tier}". Expected model, orch, or rules`);
  }
}

export function navigatePath(obj: Record<string, unknown>, path: string): unknown {
  const segments = path.split('/').filter(Boolean);
  let current: unknown = obj;

  for (const segment of segments) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

export function applyBindings(template: unknown, bindings: Record<string, string>): unknown {
  if (typeof template === 'string') {
    let result = template;
    for (const [key, value] of Object.entries(bindings)) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }
    return result;
  }

  if (Array.isArray(template)) {
    return template.map((item) => applyBindings(item, bindings));
  }

  if (template != null && typeof template === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(template as Record<string, unknown>)) {
      const boundKey =
        typeof key === 'string' ? (applyBindings(key, bindings) as string) : key;
      result[boundKey] = applyBindings(value, bindings);
    }
    return result;
  }

  return template;
}
